import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';

const BottomSheet = ({ isOpen, onClose, title, children, height = 'auto' }) => {
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll and reset scroll position
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  const bottomSheetContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            data-testid="bottom-sheet-overlay"
          />

          {/* Bottom Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`md:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)] z-[9999] flex flex-col ${
              height === 'full' ? 'top-16' : height === 'tall' ? 'max-h-[85vh]' : 'max-h-[90vh]'
            }`}
            style={{ 
              position: 'fixed', 
              bottom: 0, 
              left: 0, 
              right: 0,
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
            data-testid="bottom-sheet"
          >
            {/* Drag Handle */}
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-2 flex-shrink-0" />

            {/* Header */}
            {title && (
              <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 flex-shrink-0">
                <h2 className="text-xl font-bold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {title}
                </h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center active:scale-90 transition-transform"
                  aria-label="Close"
                >
                  <X size={24} className="text-gray-400" />
                </button>
              </div>
            )}

            {/* Content - Flexible container */}
            <div className="flex-1 min-h-0">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(bottomSheetContent, document.body);
};

export default BottomSheet;